/**
 * NotificationBell Component
 * Bell icon with badge and dropdown for notifications
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck } from 'lucide-react';
import {
  useNotifications,
  useNotificationCount,
  useAcknowledgeNotification,
  useAcknowledgeAll,
  useNotificationsRealtimeSync,
} from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { resolveNotificationPath } from '@resida/notification-types';

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // BR-15 — no polling; refreshes reactively on any bridged domain event instead.
  useNotificationsRealtimeSync();

  const { data: countData } = useNotificationCount();
  const { data: notificationsData, isLoading } = useNotifications({
    acknowledged: false,
    limit: 10,
  });

  const acknowledgeMutation = useAcknowledgeNotification();
  const acknowledgeAllMutation = useAcknowledgeAll();

  const unacknowledgedCount = countData?.count ?? 0;
  const notifications = notificationsData?.notifications ?? [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedButton = buttonRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);

      if (!clickedButton && !clickedDropdown) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Auto-close dropdown when all notifications are acknowledged. Only once the count has
  // actually loaded: before then `unacknowledgedCount` is the `?? 0` placeholder, not a real
  // zero, and a fast list response would otherwise slam the dropdown shut right after the
  // user opened it (the count query is a separate request from the list).
  const countLoaded = countData !== undefined;
  useEffect(() => {
    if (isOpen && countLoaded && unacknowledgedCount === 0 && !isLoading) {
      setIsOpen(false);
    }
  }, [unacknowledgedCount, countLoaded, isOpen, isLoading]);

  const handleAcknowledge = (id: string) => {
    acknowledgeMutation.mutate(id, {
      onSuccess: () => {
        // Close dropdown if this was the last notification
        if (notifications.length === 1) {
          setIsOpen(false);
        }
      },
    });
  };

  const handleNotificationRowActivate = (notification: (typeof notifications)[number]) => {
    setIsOpen(false);
    navigate(resolveNotificationPath(notification.referenceType, notification.referenceId));
    acknowledgeMutation.mutate(notification.id);
  };

  const handleAcknowledgeAll = () => {
    acknowledgeAllMutation.mutate(undefined, {
      onSuccess: () => {
        setIsOpen(false);
      },
    });
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-muted-foreground bg-transparent border-none rounded-full cursor-pointer transition-colors hover:text-foreground hover:bg-muted min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label={`Notifications${unacknowledgedCount > 0 ? ` (${unacknowledgedCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unacknowledgedCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold text-destructive-foreground bg-destructive rounded-full translate-x-1/2 -translate-y-1/2 animate-pulse">
            {unacknowledgedCount > 99 ? '99+' : unacknowledgedCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-80 bg-card rounded-lg border border-border z-[9999] overflow-hidden"
          style={{
            top: `${(buttonRef.current?.getBoundingClientRect().bottom ?? 0) + 8}px`,
            right: `${window.innerWidth - (buttonRef.current?.getBoundingClientRect().right ?? 0)}px`,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-foreground m-0 text-base">
              Notifications
            </h3>
            {unacknowledgedCount > 0 && (
              <button
                onClick={handleAcknowledgeAll}
                disabled={acknowledgeAllMutation.isPending}
                className="text-sm text-primary bg-transparent border-none cursor-pointer flex items-center gap-1 transition-colors hover:text-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No new notifications</p>
              </div>
            ) : (
              <ul className="border-t border-border m-0 p-0 list-none">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className="border-b border-border transition-colors hover:bg-accent"
                  >
                    <div className="flex items-start gap-3 px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleNotificationRowActivate(notification)}
                        data-testid={`notification-row-${notification.id}`}
                        aria-label={`${notification.title}: ${notification.message}`}
                        className="flex-1 min-w-0 text-left bg-transparent border-none p-0 cursor-pointer min-h-[44px]"
                      >
                        <p className="text-sm font-medium text-foreground m-0">
                          {notification.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 m-0">
                          {notification.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 m-0">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </p>
                      </button>
                      <button
                        onClick={() => handleAcknowledge(notification.id)}
                        disabled={acknowledgeMutation.isPending}
                        className="flex-shrink-0 p-1 text-muted-foreground bg-transparent border-none rounded cursor-pointer transition-colors hover:text-foreground hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title="Mark as read"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
